import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { forwardRef, ReactNode } from "react";

interface NavbarBadgeProps {
	/** Value for the data-component attribute on the pill. */
	dataComponent: string;
	/** Signal colour; omitted badges get the neutral wash. */
	bgcolor?: string;
	children: ReactNode;
}

/**
 * The monospace pill used for every environment badge in the navbar.
 *
 * forwardRef because these badges are wrapped in MUI Tooltip, which needs a
 * ref on the element it anchors to.
 */
export const NavbarBadge = forwardRef<HTMLDivElement, NavbarBadgeProps>(
	({ dataComponent, bgcolor, children, ...tooltipProps }, ref) => {
		const theme = useTheme();
		return (
			<Box
				{...tooltipProps}
				ref={ref}
				sx={{
					px: 1.25,
					py: 0.5,
					borderRadius: 1,
					bgcolor: bgcolor ?? alpha(theme.palette.common.white, 0.15),
					fontFamily: "monospace",
					fontSize: "0.85rem",
					color: alpha(theme.palette.common.white, 0.9),
					whiteSpace: "nowrap",
					cursor: "default",
					display: "flex",
					alignItems: "center",
					gap: 0.5,
				}}
				data-component={dataComponent}
			>
				{children}
			</Box>
		);
	}
);

NavbarBadge.displayName = "NavbarBadge";
